import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import { ChevronLeft, ChevronRight, CalendarDays, List, X, SlidersHorizontal, ChevronDown, Calculator } from 'lucide-react';
import TablePagination from '@mui/material/TablePagination';

import { getAttendanceCalendar, getRulesEffectiveFrom } from '../../services/timesheetService';
import api from '../../services/api';
import timesheetStatusColors from '../../utils/timesheetStatusColors';
import TIMESHEET_STATUS_LABEL_KEYS from '../../utils/timesheetStatusLabels';
import formatMinutes from '../../utils/formatMinutes';
import { anomalyLabel } from '../../utils/anomalyLabels';
import { parseTableParams, formatDate } from '../../utils/Utils';
import isInsidePortalLayer from '../../utils/isInsidePortalLayer';
import { useAuth } from '../../context/AuthContext';
import usePollCalcJob from '../../hooks/usePollCalcJob';
import useTimesheetApi from './config';

import Button from '../../components/ui/Button';
import IconButton from '../../components/ui/IconButton';
import PersonCell from '../../components/ui/PersonCell';
import Tooltip from '../../components/ui/Tooltip';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import DataTable from '../../components/ui/DataTable';
import DateTimePicker from '../../components/ui/DateTimePicker';
import ReportExportButtons from '../../components/ui/ReportExportButtons';
import { buildReportLabels, statusReportLabels, anomalyReportLabels } from '../../utils/reportLabels';
import DaySessionsRow from './components/DaySessionsRow';

// ─── Helpers ──────────────────────────────────────────────────────

const formatTime = (val) => {
  if (!val) return '';
  if (typeof val === 'string' && val.includes(':')) return val.substring(0, 5);
  return val;
};

// Hesapla çekmecesinin tarih değeri → "yyyy-MM-dd" (DateTimePicker date modu zaten bu biçimi yayar; Date
// gelirse YEREL gün alınır — UTC'ye çevirip gün kaydırmaz).
export const toIsoDay = (val) => {
  if (!val) return null;
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.slice(0, 10);
  const d = val instanceof Date ? val : new Date(val);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// İnceleme Rec3 — seçilen başlangıç yeni puantaj kurallarının yürürlük tarihinden ÖNCE mi? (engellemez, uyarır)
export const startsBeforeEffective = (startDate, effectiveFrom) => {
  const start = toIsoDay(startDate);
  const effective = toIsoDay(effectiveFrom);
  return !!start && !!effective && start < effective;
};

// "yyyy-MM-dd" yerel gün olarak, sayfanın diğer tarihleriyle aynı biçimde (utils formatDate, dil yerelinde).
const formatIsoDay = (iso, lang) => {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '-';
  return formatDate(new Date(y, m - 1, d), lang);
};

// Tek durum haritası (utils/timesheetStatusLabels) — liste görünümüyle aynı etiketler.
const STATUS_LABELS = TIMESHEET_STATUS_LABEL_KEYS;

const LEGEND_ITEMS = [
  { key: 'normal', labelKey: 'pdks.legendNormal' },
  { key: 'absent', labelKey: 'pdks.legendAbsent' },
  { key: 'leave', labelKey: 'pdks.legendLeave' },
  { key: 'partialLeave', labelKey: 'pdks.legendPartialLeave' },
  { key: 'holiday', labelKey: 'pdks.legendHoliday' },
  { key: 'halfDay', labelKey: 'pdks.legendHalfDay' },
  { key: 'missingExit', labelKey: 'pdks.legendMissingExit' },
  // 'overtime' BİLİNÇLİ OLARAK YOK — motor artık bu durumu üretmiyor (Plan 2).
  { key: 'dayOff', labelKey: 'pdks.legendDayOff' },
  { key: 'workedOnDayOff', labelKey: 'pdks.legendWorkedOnDayOff' },
];

// ─── Styled Components ───────────────────────────────────────────

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  font-family: ${({ theme }) => theme.metrics.font.family};
`;

// Üst şerit (yeniden tasarım, müşteri geri bildirimi 2026-08-12: "scope seçimi ve
// diğer aksiyon butonları sezgisel değil ve karışık görünüyor").
//
// Eskiden üç geniş çoklu-seçim + ayrı "Filtrele" düğmesi tek satırı doldurup taşıyor,
// ay navigasyonu ise ayrı bir satırda ortalanmış duruyordu. Artık tek şerit ve üç
// belirgin bölge var: DÖNEM (solda, sayfanın asıl ekseni) · KAPSAM (açılır panelde,
// seçili sayı şeritte özetlenir) · GÖRÜNÜM + AKSİYONLAR (sağda).
// "Filtrele" adımı kalktı: kapsam değişimi anında uygulanır — Çalışan Raporları
// şeridiyle aynı ilke.
const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  background: #fff;
  border: 1.5px solid #E0E4E8;
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 12px;
  position: relative;
  z-index: 20;
`;

const PeriodGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: none;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1.5px solid #E0E4E8;
  border-radius: 8px;
  background: #fff;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.main};
    color: ${({ theme }) => theme.colors.primary.main};
    background: rgba(2, 145, 239, 0.06);
  }
`;

const MonthLabel = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  min-width: 128px;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

const LinkBtn = styled.button`
  font-family: inherit;
  font-size: 12.5px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  border: 0;
  background: none;
  cursor: pointer;
  color: #6E8B8E;
  padding: 4px 6px;

  &:hover { color: ${({ theme }) => theme.colors.primary.main}; text-decoration: underline; }
`;

const Divider = styled.span`
  width: 1px;
  align-self: stretch;
  background: #E0E4E8;
  flex: none;
`;

const ScopeWrap = styled.div`
  position: relative;
  flex: none;
`;

const ScopeBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  border: 1.5px ${({ $active }) => ($active ? 'solid' : 'dashed')} ${({ $active, theme }) => ($active ? theme.colors.primary.main : '#C2CFD4')};
  background: ${({ $active }) => ($active ? 'rgba(2, 145, 239, 0.07)' : 'none')};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary.main : '#4C6F73')};
  border-radius: 999px;
  padding: 5px 12px;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.main};
    color: ${({ theme }) => theme.colors.primary.main};
  }
`;

const ScopeCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: ${({ theme }) => theme.colors.primary.main};
  color: #fff;
  font-size: 11px;
`;

const ScopePanel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 320px;
  background: #fff;
  border: 1.5px solid #E0E4E8;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(0, 66, 66, 0.16);
  z-index: 60;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// Kapsam panelinin ve hesaplama drawer'ının kapsam bölümünün başlığı — hangi kapsamın neyi
// etkilediğini ayırt eder (sayfa kapsamı ≠ hesaplama kapsamı).
const PanelHeading = styled.div`
  font-size: 12.5px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PanelHint = styled.div`
  margin-top: 3px;
  font-size: 11.5px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.regular};
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary || '#64748B'};
`;

const ScopeSummary = styled.span`
  font-size: 12.5px;
  color: #4C6F73;
  white-space: nowrap;
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex: none;
`;

const ViewToggle = styled.div`
  display: inline-flex;
  background: #EEF2F4;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
`;

const ViewBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border: 0;
  border-radius: 6px;
  background: ${({ $active }) => ($active ? '#fff' : 'none')};
  box-shadow: ${({ $active }) => ($active ? '0 1px 2px rgba(0, 66, 66, 0.12)' : 'none')};
  color: ${({ $active, theme }) => ($active ? theme.colors.text.primary : '#8A9BA0')};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 12.5px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover { color: ${({ theme }) => theme.colors.text.primary}; }
`;

const LegendBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px 0 8px;
  flex-wrap: wrap;
`;

const LegendLabel = styled.span`
  font-size: 11px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.secondary || '#64748B'};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const LegendDot = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 3px;
  background: ${({ $bg }) => $bg};
`;

// ─── Calendar Grid Styles ────────────────────────────────────────

const GridWrapper = styled.div`
  flex: 1;
  overflow: auto;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  background: #fff;
`;

const GridTable = styled.div`
  display: grid;
  grid-template-columns: 220px repeat(${({ $days }) => $days}, 36px) repeat(3, 88px);
  min-width: ${({ $days }) => 220 + $days * 36 + 3 * 88}px;
`;

const SummaryHeaderCell = styled.div`
  padding: 8px 6px;
  font-size: 10px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.secondary || '#64748B'};
  background: ${({ theme }) => theme.colors.bgColor};
  border-bottom: 2px solid #E8ECF0;
  border-left: 1px solid #E8ECF0;
  text-align: center;
  line-height: 1.2;
  white-space: normal;
`;

const SummaryCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #F1F5F9;
  border-left: 1px solid #E8ECF0;
  font-size: 12px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const HeaderCell = styled.div`
  padding: 8px 0;
  font-size: 11px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.secondary || '#64748B'};
  background: ${({ theme }) => theme.colors.bgColor};
  border-bottom: 2px solid #E8ECF0;
  text-align: center;
  white-space: nowrap;
  &:first-child {
    text-align: left;
    padding-left: 16px;
    position: sticky;
    left: 0;
    z-index: 3;
  }
`;

const EmployeeCell = styled.div`
  padding: 4px 16px;
  display: flex;
  align-items: center;
  position: sticky;
  left: 0;
  z-index: 2;
  background: #fff;
  border-bottom: 1px solid #F1F5F9;
  border-right: 1px solid #E8ECF0;
  min-height: 36px;
`;

const DayCell = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #F1F5F9;
  border-right: 1px solid #F1F5F9;
  &:last-child { border-right: none; }
`;

const DayCellInner = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 4px;
  background: ${({ $bg }) => $bg || 'transparent'};
`;

const GridEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  grid-column: 1 / -1;
  padding: 60px 24px;
  color: ${({ theme }) => theme.colors.text.secondary || '#94A3B8'};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
`;

// ─── Drawer Styles ───────────────────────────────────────────────

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #E8ECF0;
`;

const DrawerTitle = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: none;
  color: ${({ theme }) => theme.colors.text.dark?.little || '#A0A8B4'};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.bgColor}; }
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const DrawerFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #E8ECF0;
`;

const FieldGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 18px;
`;

const FieldItem = styled.div`
  grid-column: span ${({ $span }) => $span || 12};
`;

// T6 — kuyruk anketi sırasında (queued/running) ve terminal başarısızlıkta form yerine gösterilir.
const CalcStatusPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 16px;
  text-align: center;
  color: ${({ theme, $error }) => ($error ? (theme.colors.auxiliar?.danger || '#E53935') : theme.colors.text.primary)};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

// Hesapla çekmecesinde satır içi uyarı (engellemez) — GenericFormDrawer `notice` alanının uyarı tonu.
const CalcWarning = styled.div`
  background: #FFF8E6;
  border-left: 3px solid #FFC107;
  border-radius: 4px;
  padding: 10px 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;
`;

const CalcStatusDetail = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.regular};
  color: ${({ theme }) => theme.colors.text.secondary || '#64748B'};
`;

// ─── Main Component ──────────────────────────────────────────────

export default function PuantajPage() {
  const { t, i18n } = useTranslation();
  const { canWrite } = useAuth();

  // View toggle
  const [activeView, setActiveView] = useState('calendar');
  const [scopeOpen, setScopeOpen] = useState(false);
  const scopeRef = useRef(null);

  // Month navigation
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());

  // Filters (shared across views)
  const [departmentIds, setDepartmentIds] = useState([]);
  const [locationIds, setLocationIds] = useState([]);
  const [userGroupIds, setUserGroupIds] = useState([]);
  // AKM POC §7.5 (plan kararı 8) — şeride Personel Statüsü ve Kişi (birleşim üyesi) ile Vardiya (daraltan) eklendi.
  const [employeeStatusIds, setEmployeeStatusIds] = useState([]);
  const [employeeIds, setEmployeeIds] = useState([]);
  const [workHourIds, setWorkHourIds] = useState([]);

  // Calendar data — AKM POC §7.3: kapsam seçimi ZORUNLU DEĞİL; takvim sayfalıdır (ad sırası).
  const [calendarData, setCalendarData] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarFailed, setCalendarFailed] = useState(false);
  // İnceleme M3 — takvim sayfası, ait olduğu dönem + kapsam anahtarıyla birlikte tutulur: kapsam/dönem değişince sayfa
  // AYNI render'da 0'a düşer (eski sayfa için ikinci bir istek gitmez); sıra numarası geç gelen eski yanıtı yok sayar.
  const [calendarPaging, setCalendarPaging] = useState({ key: '', page: 0 });
  const [calendarPageSize, setCalendarPageSize] = useState(50);
  const calendarSeq = useRef(0);
  const [calendarTotal, setCalendarTotal] = useState(0);

  // List data — DataTable'ın KENDİ durumu (sayfa/sıralama/kolon filtreleri) ayrı tutulur;
  // üst şeritteki dönem ve kapsam bunun üzerine listQueryParams'ta birleştirilir (aşağıda).
  const [tableParams, setTableParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  // Calculate drawer
  const [calcDrawerOpen, setCalcDrawerOpen] = useState(false);
  const [calcForm, setCalcForm] = useState({
    startDate: null,
    endDate: null,
    departmentIds: [],
    locationIds: [],
    userGroupIds: [],
    employeeIds: [],
  });
  // İnceleme Rec3 — yeni puantaj kurallarının yürürlük tarihi (çekmece her açıldığında okunur).
  const [rulesEffectiveFrom, setRulesEffectiveFrom] = useState(null);
  // T6/Plan3 — calculate-job anketleme davranışı usePollCalcJob hook'una çıkarıldı; Eksik Kayıtlar
  // sayfası da AYNI akışı paylaşır. Durumlar (calculating/calcFailed/calcErrorKey/calcTimedOut),
  // 2sn interval, 5dk üst sınır, terminal davranış ve unmount temizliği birebir korunur.
  const {
    calculating, calcFailed, calcErrorKey, calcTimedOut, calcSucceeded, calcProcessedCount,
    clearCalcPoll, resetCalcState, runCalculate,
  } = usePollCalcJob();

  // ─── Derived ───────────────────────────────────────────────────

  const hasFilter = departmentIds.length > 0 || locationIds.length > 0 || userGroupIds.length > 0
    || employeeStatusIds.length > 0 || employeeIds.length > 0 || workHourIds.length > 0;
  const scopeCount = departmentIds.length + locationIds.length + userGroupIds.length
    + employeeStatusIds.length + employeeIds.length + workHourIds.length;

  // Panel kapalıyken de neyin seçili olduğu şeritte okunur kalsın.
  const scopeSummary = useMemo(() => {
    const parts = [];
    if (departmentIds.length) parts.push(`${departmentIds.length} ${t('department.title')}`);
    if (locationIds.length) parts.push(`${locationIds.length} ${t('location.title')}`);
    if (userGroupIds.length) parts.push(`${userGroupIds.length} ${t('userGroup.title')}`);
    if (employeeStatusIds.length) parts.push(`${employeeStatusIds.length} ${t('pdks.scopeEmployeeStatus')}`);
    if (employeeIds.length) parts.push(`${employeeIds.length} ${t('pdks.scopeEmployees')}`);
    if (workHourIds.length) parts.push(`${workHourIds.length} ${t('pdks.workHourFilter')}`);
    return parts.join(' · ');
  }, [departmentIds, locationIds, userGroupIds, employeeStatusIds, employeeIds, workHourIds, t]);

  // Şerit (kapsam) parametreleri — takvim, liste ve dışa aktarma AYNI şeridi gönderir (axios dizileri "ad[]=…" yazar).
  const stripParams = useMemo(() => {
    const params = {};
    if (departmentIds.length) params.departmentIds = departmentIds;
    if (locationIds.length) params.locationIds = locationIds;
    if (userGroupIds.length) params.userGroupIds = userGroupIds;
    if (employeeStatusIds.length) params.employeeStatusIds = employeeStatusIds;
    if (employeeIds.length) params.employeeIds = employeeIds;
    if (workHourIds.length) params.workHourIds = workHourIds;
    return params;
  }, [departmentIds, locationIds, userGroupIds, employeeStatusIds, employeeIds, workHourIds]);

  // İnceleme M3 — geçerli takvim sayfası: kayıtlı sayfa başka bir dönem/kapsam anahtarına aitse 0 (tek istek).
  const calendarScopeKey = useMemo(() => JSON.stringify([year, month, stripParams]), [year, month, stripParams]);
  const calendarPage = calendarPaging.key === calendarScopeKey ? calendarPaging.page : 0;
  const setCalendarPage = useCallback(
    (page) => setCalendarPaging({ key: calendarScopeKey, page }),
    [calendarScopeKey],
  );

  const daysInMonth = useMemo(() => new Date(year, month + 1, 0).getDate(), [year, month]);
  const dayNumbers = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth]);
  const monthLabel = useMemo(() => `${t(`months.${month}`)} ${year}`, [t, month, year]);

  // Liste sorgusu = tablo durumu + üst şeritteki DÖNEM + KAPSAM.
  //
  // (a) Kapsam artık listeye de uygulanır: eskiden üst şeritten "Satış" seçip Liste'ye geçen
  //     kullanıcı tüm tenant'ın kayıtlarını görüyordu (kapsam sessizce yok sayılıyordu).
  // (b) Birleştirme TÜRETİLMİŞ bir memo'da yapılır, state'e YAZILMAZ: parseTableParams her tablo
  //     etkileşiminde parametre nesnesinin TAMAMINI yeniden üretir; dönem/kapsam doğrudan o
  //     nesnenin içine yazılsaydı ilk sayfa/sıralama değişiminde sessizce düşerdi.
  const listQueryParams = useMemo(() => {
    const monthStart = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const monthEnd = `${year}-${String(month + 1).padStart(2, '0')}-${String(daysInMonth).padStart(2, '0')}`;
    // Kullanıcı tablonun kendi tarih filtresini kullandıysa şeridin ayı DEVREDEN ÇIKAR: iki
    // kaynağın tarih anahtarlarını karıştırmak (şeritten date[gte], tablodan date[between])
    // sunucuda birinin sessizce yok sayılmasına yol açardı.
    const tableHasDateFilter = Object.keys(tableParams).some((k) => k.startsWith('date['));
    const params = tableHasDateFilter
      ? { ...tableParams }
      : { ...tableParams, 'date[gte]': monthStart, 'date[lte]': monthEnd };
    return { ...params, ...stripParams };
  }, [tableParams, year, month, daysInMonth, stripParams]);

  const {
    fetching: listFetching, columns, data: listData, pagination, refresh: listRefresh, statusLabels: listStatusLabels,
  } = useTimesheetApi(listQueryParams);
  // Liste dışa aktarması: sütun başlıkları + gün durumu ve anomali adları web dilinde.
  const getListLabels = useCallback(() => buildReportLabels(t, columns, t('timesheet.title'), {
    ...statusReportLabels(listStatusLabels),
    ...anomalyReportLabels(t),
  }), [t, columns, listStatusLabels]);

  // ─── Search helpers ────────────────────────────────────────────

  const searchDepartments = useCallback(async (query) => {
    try {
      const params = query ? { 'name[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/departments', { params });
      const result = res.data?.data;
      return Array.isArray(result) ? result : (result?.data || []);
    } catch { return []; }
  }, []);

  const searchLocations = useCallback(async (query) => {
    try {
      const params = query ? { 'name[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/locations', { params });
      const result = res.data?.data;
      return Array.isArray(result) ? result : (result?.data || []);
    } catch { return []; }
  }, []);

  const searchUserGroups = useCallback(async (query) => {
    try {
      const params = query ? { 'name[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/user-groups', { params });
      const result = res.data?.data;
      return Array.isArray(result) ? result : (result?.data || []);
    } catch { return []; }
  }, []);

  const searchEmployees = useCallback(async (query) => {
    try {
      const params = query ? { 'firstName[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const result = res.data?.data;
      return Array.isArray(result) ? result : (result?.data || []);
    } catch { return []; }
  }, []);

  const searchEmployeeStatuses = useCallback(async (query) => {
    try {
      const params = query ? { 'name[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employee-statuses', { params });
      const result = res.data?.data;
      return Array.isArray(result) ? result : (result?.data || []);
    } catch { return []; }
  }, []);

  const searchWorkHours = useCallback(async (query) => {
    try {
      const params = query ? { 'name[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/work-hours', { params });
      const result = res.data?.data;
      return Array.isArray(result) ? result : (result?.data || []);
    } catch { return []; }
  }, []);

  const searchPeople = useCallback(async (query) => {
    try {
      const params = query ? { 'fullName[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const result = res.data?.data;
      const list = Array.isArray(result) ? result : (result?.data || []);
      return list.map((e) => ({ ...e, name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim() }));
    } catch { return []; }
  }, []);

  // ─── Calendar fetch ────────────────────────────────────────────

  // AKM POC §7.3 — kapsam seçmeden de yüklenir; sayfalı (ad sırası). Yanıt PaginatedList; eski düz liste de okunur.
  // api.js GET'te reddetmez: res.data.status === false → "yüklenemedi" (boş takvim değil).
  const fetchCalendar = useCallback(async () => {
    const seq = ++calendarSeq.current;
    setCalendarLoading(true);
    try {
      const params = { year, month: month + 1, pageNumber: calendarPage + 1, pageSize: calendarPageSize, ...stripParams };
      const res = await getAttendanceCalendar(params);
      if (seq !== calendarSeq.current) return;   // daha yeni bir istek çıktı — bu yanıt bayat
      if (res?.data?.status === false) {
        setCalendarFailed(true);
        setCalendarData([]);
        setCalendarTotal(0);
        return;
      }
      const result = res?.data?.data;
      const rows = Array.isArray(result) ? result : (result?.data || []);
      setCalendarFailed(false);
      setCalendarData(rows);
      setCalendarTotal(Array.isArray(result) ? rows.length : (result?.totalRowCount ?? rows.length));
    } catch {
      if (seq !== calendarSeq.current) return;
      setCalendarFailed(true);
      setCalendarData([]);
    } finally {
      // Bayat isteğin finally'si yükleniyor bayrağını yeni isteğin üzerinden kapatmasın.
      if (seq === calendarSeq.current) setCalendarLoading(false);
    }
  }, [year, month, calendarPage, calendarPageSize, stripParams]);

  // Dönem veya kapsam değiştiğinde ANINDA uygulanır — ayrı bir "Filtrele" adımı yok. Takvim ilk sayfaya döner ve
  // yeniden çekilir; listenin parametreleri listQueryParams memo'sundan türer, burada yalnız sayfa başa alınır.
  useEffect(() => {
    setTableParams((prev) => (prev.pageNumber === 1 ? prev : { ...prev, pageNumber: 1 }));
  }, [year, month, stripParams]);

  useEffect(() => {
    if (activeView === 'calendar') fetchCalendar();
  }, [activeView, fetchCalendar]);

  // Kapsam paneli: dışarı tıklayınca/ESC ile kapanır.
  //
  // "Dışarısı"na portal katmanları DAHİL DEĞİLDİR: panelin içindeki AsyncGenericSelect açılır
  // listesini document.body'ye basar, yani scopeRef'in DOM alt ağacının dışında kalır. Bu ayrım
  // gözetilmezse seçeneğe basılan İLK mousedown paneli kapatır, panel kapanınca select unmount
  // olur ve tıklama (mousedown → mouseup → click) hiç tamamlanmaz — üst şeritten kapsam seçmek
  // imkânsız hale gelir (bkz. utils/isInsidePortalLayer + PuantajScopePanel.test.js).
  useEffect(() => {
    if (!scopeOpen) return undefined;
    const onDown = (e) => {
      if (isInsidePortalLayer(e.target)) return;
      if (scopeRef.current && !scopeRef.current.contains(e.target)) setScopeOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setScopeOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [scopeOpen]);

  const clearScope = useCallback(() => {
    setDepartmentIds([]);
    setLocationIds([]);
    setUserGroupIds([]);
    setEmployeeStatusIds([]);
    setEmployeeIds([]);
    setWorkHourIds([]);
  }, []);

  // ─── Month navigation ─────────────────────────────────────────

  const handlePrevMonth = useCallback(() => {
    setMonth((prev) => {
      if (prev === 0) { setYear((y) => y - 1); return 11; }
      return prev - 1;
    });
  }, []);

  const handleNextMonth = useCallback(() => {
    setMonth((prev) => {
      if (prev === 11) { setYear((y) => y + 1); return 0; }
      return prev + 1;
    });
  }, []);

  const handleToday = useCallback(() => {
    const today = new Date();
    setYear(today.getFullYear());
    setMonth(today.getMonth());
  }, []);

  // AKM POC §7.1 / §7.3 — dışa aktarma etiketleri (kullanıcının dili): takvim → Aylık Puantaj Cetveli, liste → puantaj
  // listesi. Sütun başlıkları tablodan, ortak etiketler reportExport.labels.*'tan.
  const calendarExportParams = useMemo(() => ({ year, month: month + 1, ...stripParams }), [year, month, stripParams]);
  // Aylık Puantaj Cetveli dosyasının sütun, toplam ve açıklama (lejant) başlıkları web dilinde.
  const getCalendarLabels = useCallback(() => buildReportLabels(t, [], t('taReports.monthlyRegister.export'), {
    registrationNumber: t('taReports.attendanceCard.registrationNumber'),
    userFullName: t('timesheet.employee'),
    departmentName: t('taReports.attendanceCard.department'),
    workedDays: t('taReports.attendanceCard.workedDays'),
    workedMinutes: t('taReports.attendanceCard.workedMinutes'),
    leaveDays: t('taReports.attendanceCard.leaveDays'),
    medicalLeaveDays: t('taReports.attendanceCard.medicalLeaveDays'),
    hourlyLeaveMinutes: t('payroll.hourlyLeaveMinutes'),
    absentDays: t('taReports.attendanceCard.absentDays'),
    overtimeMinutes: t('taReports.columns.computedOvertime'),
    approvedOvertimeMinutes: t('taReports.columns.approvedOvertime'),
    undertimeMinutes: t('taReports.undertime.column'),
    weeklyRestWorkMinutes: t('payroll.weeklyRestWorkMinutes'),
    holidayWorkMinutes: t('payroll.holidayWorkMinutes'),
    'legend.worked': t('taReports.attendanceCard.workedMinutes'),
    'legend.medical': t('taReports.monthlyRegister.legendMedical'),
    'legend.partialMedical': t('taReports.monthlyRegister.legendPartialMedical'),
    'legend.halfDayHoliday': t('taReports.monthlyRegister.legendHalfDayHoliday'),
    'legend.halfDayLeave': t('taReports.monthlyRegister.legendHalfDayLeave'),
    'legend.halfDayMedical': t('taReports.monthlyRegister.legendHalfDayMedical'),
    ...statusReportLabels(listStatusLabels),
  }), [t, listStatusLabels]);

  // ─── Calculate handlers ────────────────────────────────────────

  const handleOpenCalc = useCallback(() => {
    clearCalcPoll();
    setCalcForm({
      startDate: null,
      endDate: null,
      departmentIds: [],
      locationIds: [],
      userGroupIds: [],
      employeeIds: [],
    });
    resetCalcState();
    setCalcDrawerOpen(true);
  }, [clearCalcPoll, resetCalcState]);

  // Yürürlük tarihi çekmece açılınca okunur. api.js GET'te reddetmez — sinyal res.data.status; okunamazsa
  // uyarı gösterilmez (bilgi amaçlı, hesaplamayı engellemez).
  useEffect(() => {
    if (!calcDrawerOpen) return undefined;
    let cancelled = false;
    setRulesEffectiveFrom(null);
    Promise.resolve(getRulesEffectiveFrom()).then((res) => {
      if (cancelled) return;
      setRulesEffectiveFrom(res?.data?.status === true ? (res.data.data?.effectiveFrom || null) : null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [calcDrawerOpen]);

  const calcBeforeEffective = startsBeforeEffective(calcForm.startDate, rulesEffectiveFrom);

  const handleCloseCalc = useCallback(() => {
    // Poll sürüyorken kapatılırsa durdurulur — iş sunucu tarafında zaten devam eder/biter, sadece
    // FE anketi durur (kapalı drawer'da gereksiz istek atmanın anlamı yok).
    clearCalcPoll();
    setCalcDrawerOpen(false);
    resetCalcState();
  }, [clearCalcPoll, resetCalcState]);

  const handleCalculate = useCallback(async () => {
    const toDateOnly = (val) => {
      if (!val) return null;
      const d = new Date(val);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    const payload = {
      startDate: toDateOnly(calcForm.startDate),
      endDate: toDateOnly(calcForm.endDate),
    };
    if (calcForm.employeeIds.length > 0) payload.employeeIds = calcForm.employeeIds;
    if (calcForm.departmentIds.length > 0) payload.departmentIds = calcForm.departmentIds;
    if (calcForm.locationIds.length > 0) payload.locationIds = calcForm.locationIds;
    if (calcForm.userGroupIds.length > 0) payload.userGroupIds = calcForm.userGroupIds;

    // Başarıda drawer KAPANMAZ: sonucu bildiren tek şey oydu ve kapanınca ekranda hiçbir şey
    // değişmiyordu (kapsam seçilmemişse takvim zaten veri çekmez) — kullanıcı hesaplamanın
    // çalışıp çalışmadığını göremiyordu. Artık drawer içinde "tamamlandı + kaç çalışan" durumu
    // gösterilir, kapatmayı kullanıcı yapar. Arkadaki görünüm yine de hemen tazelenir.
    runCalculate(payload, {
      onSucceeded: () => {
        if (activeView === 'calendar') {
          fetchCalendar();
        } else {
          listRefresh();
        }
      },
    });
  }, [calcForm, activeView, fetchCalendar, listRefresh, runCalculate]);

  // ─── List view handlers ────────────────────────────────────────

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, columns);
    setTableParams(params);
  }, [columns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  // ─── Calendar helpers ──────────────────────────────────────────

  const buildTooltip = useCallback((day) => {
    if (!day) return null;
    const parts = [];
    if (day.status) {
      const labelKey = STATUS_LABELS[day.status];
      parts.push(labelKey ? t(labelKey) : day.status);
    }
    if (day.actualMinutes != null) parts.push(`${t('timesheet.actual')}: ${formatMinutes(day.actualMinutes)}`);
    if (day.firstEntry) parts.push(`${t('pdks.firstEntry')}: ${formatTime(day.firstEntry)}`);
    if (day.lastExit) parts.push(`${t('pdks.lastExit')}: ${formatTime(day.lastExit)}`);
    if (day.lateMinutes > 0) parts.push(`${t('timesheet.late')}: ${formatMinutes(day.lateMinutes)}`);
    // backend attendance-calendar hücresi henüz outsideMinutes DÖNDÜRMÜYOR (bu planın
    // dışında — bkz. Plan 3 "Sonrası"); alan eklendiğinde bu satır otomatik devreye girer.
    if (day.outsideMinutes > 0) parts.push(`${t('timesheet.outside')}: ${formatMinutes(day.outsideMinutes)}`);
    // attendance-calendar hücresi (AttendanceCalendarCell) anomalies DÖNDÜRMÜYOR (yalnız liste
    // görünümü — GET /v1/timesheets — bu alanı taşır); guard'lı bırakıldı, alan eklendiğinde bu
    // satır otomatik devreye girer (DTO değişikliği bu planın dışında, ayrı karar).
    if (day.anomalies?.length) {
      day.anomalies.forEach((code) => parts.push(anomalyLabel(t, code)));
    }
    return parts.join(' | ');
  }, [t]);

  const findDay = useCallback((days, dayNum) => {
    if (!days) return null;
    return days.find((d) => {
      if (!d.date) return false;
      return new Date(d.date).getDate() === dayNum;
    });
  }, []);

  // ─── Render ────────────────────────────────────────────────────

  return (
    <Container>

      {/* Üst şerit: dönem · kapsam · görünüm ve aksiyonlar */}
      <TopBar>
        <PeriodGroup>
          <NavButton onClick={handlePrevMonth} aria-label={t('common.previous', { defaultValue: 'Önceki' })}>
            <ChevronLeft size={16} />
          </NavButton>
          <MonthLabel>{monthLabel}</MonthLabel>
          <NavButton onClick={handleNextMonth} aria-label={t('common.next', { defaultValue: 'Sonraki' })}>
            <ChevronRight size={16} />
          </NavButton>
          <LinkBtn onClick={handleToday}>{t('pdks.today')}</LinkBtn>
        </PeriodGroup>

        <Divider aria-hidden="true" />

        <ScopeWrap ref={scopeRef}>
          <ScopeBtn
            type="button"
            $active={hasFilter}
            aria-haspopup="dialog"
            aria-expanded={scopeOpen}
            onClick={() => setScopeOpen((o) => !o)}
          >
            <SlidersHorizontal size={14} />
            {t('timesheet.scope')}
            {scopeCount > 0 && <ScopeCount>{scopeCount}</ScopeCount>}
            <ChevronDown size={14} />
          </ScopeBtn>

          {scopeOpen && (
            <ScopePanel role="dialog" aria-label={t('timesheet.scopeViewedTitle')}>
              {/* Bu kapsam ile Hesapla drawer'ındaki kapsam AYRI şeylerdir (müşteri geri
                  bildirimi 2026-08-13: "çelişki oluşturmuyor mu"). İkisi de başlık + tek
                  cümlelik açıklama taşır ki hangisinin neyi etkilediği okunur olsun. */}
              <PanelHeading>
                {t('timesheet.scopeViewedTitle')}
                <PanelHint>{t('timesheet.scopeViewedHint')}</PanelHint>
              </PanelHeading>
              <AsyncGenericSelect
                label={t('department.title')}
                value={departmentIds}
                onChange={setDepartmentIds}
                onSearch={searchDepartments}
                isMulti
              />
              <AsyncGenericSelect
                label={t('location.title')}
                value={locationIds}
                onChange={setLocationIds}
                onSearch={searchLocations}
                isMulti
              />
              <AsyncGenericSelect
                label={t('userGroup.title')}
                value={userGroupIds}
                onChange={setUserGroupIds}
                onSearch={searchUserGroups}
                isMulti
              />
              <AsyncGenericSelect
                label={t('pdks.scopeEmployeeStatus')}
                value={employeeStatusIds}
                onChange={setEmployeeStatusIds}
                onSearch={searchEmployeeStatuses}
                isMulti
              />
              <AsyncGenericSelect
                label={t('pdks.scopeEmployees')}
                value={employeeIds}
                onChange={setEmployeeIds}
                onSearch={searchPeople}
                isMulti
              />
              {/* Vardiya seçimi DARALTIR (yalnız o vardiyalı günler / ayda o vardiyada günü olanlar); diğerleri birleşim. */}
              <AsyncGenericSelect
                label={t('pdks.workHourFilter')}
                value={workHourIds}
                onChange={setWorkHourIds}
                onSearch={searchWorkHours}
                isMulti
              />
            </ScopePanel>
          )}
        </ScopeWrap>

        {hasFilter ? (
          <>
            <ScopeSummary>{scopeSummary}</ScopeSummary>
            <LinkBtn onClick={clearScope}>{t('report.clear')}</LinkBtn>
          </>
        ) : (
          <ScopeSummary>{t('timesheet.scopeEmptyHint')}</ScopeSummary>
        )}

        <ActionGroup>
          <ViewToggle role="group" aria-label={t('timesheet.title')}>
            <ViewBtn
              type="button"
              $active={activeView === 'calendar'}
              aria-pressed={activeView === 'calendar'}
              onClick={() => setActiveView('calendar')}
            >
              <CalendarDays size={14} /> {t('timesheet.viewCalendar')}
            </ViewBtn>
            <ViewBtn
              type="button"
              $active={activeView === 'list'}
              aria-pressed={activeView === 'list'}
              onClick={() => setActiveView('list')}
            >
              <List size={14} /> {t('timesheet.viewList')}
            </ViewBtn>
          </ViewToggle>

          {activeView === 'calendar' && (
            <ReportExportButtons
              url="/v1/ta/reports/attendance-calendar/export"
              params={calendarExportParams}
              getLabels={getCalendarLabels}
              formatLabels={{
                xlsx: `${t('taReports.monthlyRegister.export')} · ${t('reportExport.xlsx')}`,
                pdf: `${t('taReports.monthlyRegister.export')} · ${t('reportExport.pdf')}`,
              }}
              disabled={calendarLoading || calendarTotal === 0}
            />
          )}

          {canWrite('TA') && (
            <Tooltip text={t('timesheet.calculate')}>
              <IconButton
                type="button"
                size="sm"
                variant="gradient"
                aria-label={t('timesheet.calculate')}
                onClick={handleOpenCalc}
              >
                <Calculator size={15} />
              </IconButton>
            </Tooltip>
          )}
        </ActionGroup>
      </TopBar>

      {/* Calendar view */}
      {activeView === 'calendar' && (
        <>
          <LegendBar>
            <LegendLabel>{t('pdks.legendTitle')}</LegendLabel>
            {LEGEND_ITEMS.map((item) => {
              const colors = timesheetStatusColors[item.key];
              return (
                <LegendItem key={item.key}>
                  <LegendDot $bg={colors?.bg || '#e2e8f0'} />
                  {t(item.labelKey)}
                </LegendItem>
              );
            })}
          </LegendBar>

          <GridWrapper>
            <GridTable $days={daysInMonth}>
              <HeaderCell>{t('pdks.employee')}</HeaderCell>
              {dayNumbers.map((d) => <HeaderCell key={d}>{d}</HeaderCell>)}
              <SummaryHeaderCell>{t('pdks.summaryWorkedDays')}</SummaryHeaderCell>
              <SummaryHeaderCell>{t('pdks.summaryWorkedMinutes')}</SummaryHeaderCell>
              <SummaryHeaderCell>{t('pdks.summaryAbsentDays')}</SummaryHeaderCell>

              {calendarLoading && <GridEmpty>{t('common.loading')}</GridEmpty>}
              {!calendarLoading && calendarFailed && <GridEmpty role="alert">{t('pdks.calendarLoadFailed')}</GridEmpty>}
              {!calendarLoading && !calendarFailed && calendarData.length === 0 && <GridEmpty>{t('common.noRecords')}</GridEmpty>}

              {!calendarLoading && calendarData.map((row) => (
                <React.Fragment key={row.userId}>
                  <EmployeeCell>
                    <PersonCell name={row.userFullName} profilePicture={row.userProfilePicture} />
                  </EmployeeCell>
                  {dayNumbers.map((dayNum) => {
                    const dayData = findDay(row.days, dayNum);
                    const colors = dayData?.status ? timesheetStatusColors[dayData.status] : null;
                    const tooltipText = buildTooltip(dayData);
                    return (
                      <DayCell key={dayNum}>
                        {dayData ? (
                          <Tooltip text={tooltipText} position="bottom">
                            <DayCellInner $bg={colors?.bg || 'transparent'} />
                          </Tooltip>
                        ) : (
                          <DayCellInner />
                        )}
                      </DayCell>
                    );
                  })}
                  <SummaryCell>{row.workedDays ?? 0}</SummaryCell>
                  <SummaryCell>{formatMinutes(row.workedMinutes ?? 0)}</SummaryCell>
                  <SummaryCell>{row.absentDays ?? 0}</SummaryCell>
                </React.Fragment>
              ))}
            </GridTable>
          </GridWrapper>
          <TablePagination
            component="div"
            count={calendarTotal}
            page={calendarPage}
            rowsPerPage={calendarPageSize}
            rowsPerPageOptions={[25, 50, 100]}
            onPageChange={(e, page) => setCalendarPage(page)}
            onRowsPerPageChange={(e) => { setCalendarPageSize(parseInt(e.target.value, 10)); setCalendarPage(0); }}
            labelRowsPerPage={t('dataTable.rowsPerPage')}
            data-testid="calendar-pagination"
          />
        </>
      )}

      {/* List view */}
      {activeView === 'list' && (
        <DataTable
          title={t('timesheet.title')}
          fetching={listFetching}
          columns={columns}
          data={listData}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          download={false}
          toolbarActions={(
            <ReportExportButtons
              url="/v1/timesheets/export"
              params={listQueryParams}
              getLabels={getListLabels}
            />
          )}
          expandableRows
          renderExpandableRow={(row) => (row ? <DaySessionsRow userId={row.userId} date={row.date} /> : null)}
        />
      )}

      {/* Calculate Drawer */}
      <Drawer
        open={calcDrawerOpen}
        onClose={handleCloseCalc}
        direction="right"
        size="45%"
        duration={150}
        overlayOpacity={0.2}
        style={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '100vw',
          minWidth: 380,
          margin: '12px',
          borderRadius: '6px',
          height: 'calc(100vh - 24px)',
          boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)',
        }}
      >
        <DrawerHeader>
          <DrawerTitle>{t('timesheet.calculateTitle')}</DrawerTitle>
          <CloseBtn onClick={handleCloseCalc}><X size={20} /></CloseBtn>
        </DrawerHeader>

        <DrawerBody>
          {calcFailed ? (
            // Terminal başarısızlık (job.status === 'failed' — GERÇEK backend hatası, ör.
            // timesheet.jobStale) — bileşen toast'ı YOK (kural), mesaj drawer İÇİNDE. calcErrorKey
            // (job.error) responseErrors ile AYNI çeviri anahtarı kullanılır.
            <CalcStatusPanel $error>
              <div>{t('timesheet.calculateFailedInline')}</div>
              {calcErrorKey && (
                <CalcStatusDetail>
                  {t(`responseErrors.${calcErrorKey}`, { defaultValue: calcErrorKey })}
                </CalcStatusDetail>
              )}
            </CalcStatusPanel>
          ) : calcTimedOut ? (
            // 5dk poll üst sınırı (I1 denetim düzeltmesi) — job hâlâ queued/running olabilir ve
            // sonradan BAŞARIYLA yazabilir; bu yüzden $error DEĞİL, calculateQueued ile AYNI nötr
            // stil kullanılır. "Başarısız" damgası YOK.
            <CalcStatusPanel>{t('timesheet.calculateStillRunning')}</CalcStatusPanel>
          ) : calculating ? (
            <CalcStatusPanel>{t('timesheet.calculateQueued')}</CalcStatusPanel>
          ) : calcSucceeded ? (
            // Başarı da GÖRÜNÜR bir durum (müşteri geri bildirimi 2026-08-13: "drawer kapanıyor
            // ama ekranda hiçbir şey değişmiyor"). Bileşen toast'ı yok — kural gereği yalnız
            // Axios interceptor toast basar — bu yüzden geri bildirim burada, satır içinde.
            <CalcStatusPanel>
              <div>{t('timesheet.calculateSucceeded')}</div>
              <CalcStatusDetail>
                {t('timesheet.calculateSucceededDetail', { count: calcProcessedCount })}
              </CalcStatusDetail>
            </CalcStatusPanel>
          ) : (
            <FieldGroup>
              <FieldItem $span={6}>
                <DateTimePicker
                  label={t('timesheet.startDate')}
                  value={calcForm.startDate}
                  onChange={(val) => setCalcForm((p) => ({ ...p, startDate: val }))}
                  mode="date"
                  required
                />
              </FieldItem>
              <FieldItem $span={6}>
                <DateTimePicker
                  label={t('timesheet.endDate')}
                  value={calcForm.endDate}
                  onChange={(val) => setCalcForm((p) => ({ ...p, endDate: val }))}
                  mode="date"
                  required
                />
              </FieldItem>
              {calcBeforeEffective && (
                <FieldItem $span={12}>
                  <CalcWarning role="status" data-testid="calc-before-effective-warning">
                    {t('timesheet.calcBeforeEffectiveWarning', {
                      date: formatIsoDay(rulesEffectiveFrom, i18n?.language || 'tr'),
                    })}
                  </CalcWarning>
                </FieldItem>
              )}
              <FieldItem $span={12}>
                {/* Sayfanın üst şeridindeki KAPSAM'dan bağımsızdır — hangisinin neyi etkilediği
                    yazıyla söylenir (müşteri geri bildirimi 2026-08-13). */}
                <PanelHeading>
                  {t('timesheet.scopeCalcTitle')}
                  <PanelHint>{t('timesheet.scopeCalcHint')}</PanelHint>
                </PanelHeading>
              </FieldItem>
              <FieldItem $span={12}>
                <AsyncGenericSelect
                  label={t('department.title')}
                  value={calcForm.departmentIds}
                  onChange={(val) => setCalcForm((p) => ({ ...p, departmentIds: val }))}
                  onSearch={searchDepartments}
                  isMulti
                />
              </FieldItem>
              <FieldItem $span={12}>
                <AsyncGenericSelect
                  label={t('location.title')}
                  value={calcForm.locationIds}
                  onChange={(val) => setCalcForm((p) => ({ ...p, locationIds: val }))}
                  onSearch={searchLocations}
                  isMulti
                />
              </FieldItem>
              <FieldItem $span={12}>
                <AsyncGenericSelect
                  label={t('userGroup.title')}
                  value={calcForm.userGroupIds}
                  onChange={(val) => setCalcForm((p) => ({ ...p, userGroupIds: val }))}
                  onSearch={searchUserGroups}
                  isMulti
                />
              </FieldItem>
              <FieldItem $span={12}>
                <AsyncGenericSelect
                  label={t('timesheet.employees')}
                  value={calcForm.employeeIds}
                  onChange={(val) => setCalcForm((p) => ({ ...p, employeeIds: val }))}
                  onSearch={searchEmployees}
                  renderOption={(opt) => (
                    <PersonCell
                      name={opt.name || `${opt.firstName || ''} ${opt.lastName || ''}`.trim()}
                      profilePicture={opt.profilePicture}
                    />
                  )}
                  isMulti
                />
              </FieldItem>
            </FieldGroup>
          )}
        </DrawerBody>

        {/* Başarı durumunda form yok — tek anlamlı aksiyon kapatmaktır. */}
        <DrawerFooter>
          {calcSucceeded ? (
            <Button onClick={handleCloseCalc}>{t('common.close')}</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={handleCloseCalc}>
                {t('timesheet.cancel')}
              </Button>
              <Button
                onClick={handleCalculate}
                disabled={calculating || !calcForm.startDate || !calcForm.endDate}
              >
                {calculating ? t('timesheet.calculating') : t('timesheet.calculate')}
              </Button>
            </>
          )}
        </DrawerFooter>
      </Drawer>
    </Container>
  );
}
